import { FC, useCallback, useState } from "react"
import { toast } from "react-toastify"
import { FieldArray, Form, Formik } from "formik"
import * as Yup from "yup"

import { Modal } from "components/modals/Modal"
import { ACTION_TYPES, CONDITION_FACTORS, LEVELS, OPERATORS, SEVERITIES } from "components/risk/constants"

import { riskService } from "services/risk/risk.service"
import { AutoActionCondition, AutoActionRule } from "services/risk/types"

interface FormValues {
  name: string
  description: string
  conditions: AutoActionCondition[]
  actions: string[]
  is_active: boolean
}

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  conditions: Yup.array()
    .of(
      Yup.object({
        factor: Yup.string().required(),
        operator: Yup.string().required()
      })
    )
    .min(1, "At least one condition is required"),
  actions: Yup.array().of(Yup.string().required()).min(1, "At least one action is required")
})

const emptyCondition: AutoActionCondition = { factor: "GENERAL_RISK", operator: ">=", level: "LEVEL_3" }

interface Props {
  rule: AutoActionRule | null
  open: boolean
  onClose: () => void
  onSave: () => void
}

export const AutoActionRuleModal: FC<Props> = ({ rule, open, onClose, onSave }) => {
  const isEdit = !!rule
  const [submitting, setSubmitting] = useState(false)

  const initialValues: FormValues = rule
    ? {
        name: rule.name,
        description: rule.description || "",
        conditions: rule.conditions,
        actions: rule.actions,
        is_active: rule.is_active
      }
    : {
        name: "",
        description: "",
        conditions: [{ ...emptyCondition }],
        actions: ["FLAG_FOR_REVIEW"],
        is_active: true
      }

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setSubmitting(true)
      try {
        if (isEdit && rule) {
          await riskService.updateAutoAction(rule.id, values)
          toast.success("Rule updated")
        } else {
          await riskService.createAutoAction(values)
          toast.success("Rule created")
        }
        onSave()
        onClose()
      } catch {
        toast.error("Failed to save rule")
      } finally {
        setSubmitting(false)
      }
    },
    [isEdit, rule, onSave, onClose]
  )

  return (
    <Modal open={open} handler={onClose}>
      <div className="modal-box max-w-2xl bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg">
        <h3 className="font-bold text-lg mb-4">{isEdit ? "Edit" : "Create"} Auto Action Rule</h3>

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
          {({ values, setFieldValue, errors, touched }) => (
            <Form className="space-y-4">
              {/* Name */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Rule Name</span>
                </label>
                <input
                  className="input input-bordered dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                  value={values.name}
                  onChange={(e) => setFieldValue("name", e.target.value)}
                  placeholder="e.g., High Risk Auto-Ban"
                />
                {touched.name && errors.name && <p className="text-error text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Description */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description (optional)</span>
                </label>
                <textarea
                  className="textarea textarea-bordered dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                  value={values.description}
                  onChange={(e) => setFieldValue("description", e.target.value)}
                  rows={2}
                />
              </div>

              {/* Conditions */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Conditions (ALL must match)</span>
                </label>
                <FieldArray name="conditions">
                  {({ push, remove }) => (
                    <div className="space-y-2">
                      {values.conditions.map((cond, index) => {
                        const isGeneral = cond.factor === "GENERAL_RISK"
                        return (
                          <div key={index} className="flex gap-2 items-center">
                            <select
                              className="select select-bordered select-sm flex-1 dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                              value={cond.factor}
                              onChange={(e) => {
                                const newFactor = e.target.value
                                const isNewGeneral = newFactor === "GENERAL_RISK"
                                setFieldValue(`conditions.${index}`, {
                                  factor: newFactor,
                                  operator: cond.operator,
                                  ...(isNewGeneral ? { level: "LEVEL_3" } : { severity: "HIGH" })
                                })
                              }}
                            >
                              {CONDITION_FACTORS.map((f) => (
                                <option key={f.value} value={f.value}>
                                  {f.label}
                                </option>
                              ))}
                            </select>

                            <select
                              className="select select-bordered select-sm w-20 dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                              value={cond.operator}
                              onChange={(e) => setFieldValue(`conditions.${index}.operator`, e.target.value)}
                            >
                              {OPERATORS.map((op) => (
                                <option key={op} value={op}>
                                  {op}
                                </option>
                              ))}
                            </select>

                            {isGeneral ? (
                              <select
                                className="select select-bordered select-sm flex-1 dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                                value={cond.level || "LEVEL_3"}
                                onChange={(e) => setFieldValue(`conditions.${index}.level`, e.target.value)}
                              >
                                {LEVELS.map((l) => (
                                  <option key={l} value={l}>
                                    {l.replace("_", " ")}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <select
                                className="select select-bordered select-sm flex-1 dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                                value={cond.severity || "HIGH"}
                                onChange={(e) => setFieldValue(`conditions.${index}.severity`, e.target.value)}
                              >
                                {SEVERITIES.map((s) => (
                                  <option key={s} value={s}>
                                    {s.replace("_", " ")}
                                  </option>
                                ))}
                              </select>
                            )}

                            {values.conditions.length > 1 && (
                              <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={() => remove(index)}>
                                ✕
                              </button>
                            )}
                          </div>
                        )
                      })}
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => push({ ...emptyCondition })}>
                        + Add Condition
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* Actions */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Actions</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {ACTION_TYPES.map((a) => {
                    const selected = values.actions.includes(a.value)
                    return (
                      <button
                        key={a.value}
                        type="button"
                        className={`badge badge-lg cursor-pointer ${selected ? "badge-primary" : "badge-outline"}`}
                        onClick={() => {
                          const next = selected ? values.actions.filter((v) => v !== a.value) : [...values.actions, a.value]
                          setFieldValue("actions", next)
                        }}
                      >
                        {a.label}
                      </button>
                    )
                  })}
                </div>
                {touched.actions && errors.actions && <p className="text-error text-sm mt-1">At least one action is required</p>}
              </div>

              {/* Active toggle */}
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-4">
                  <input
                    type="checkbox"
                    className="toggle toggle-success"
                    checked={values.is_active}
                    onChange={(e) => setFieldValue("is_active", e.target.checked)}
                  />
                  <span className="label-text">Rule is active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="modal-action">
                <button type="button" className="btn btn-ghost" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Saving..." : isEdit ? "Update Rule" : "Create Rule"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
